import { useSensorHistory } from '@gozoki/api';
import { Box, Button, ButtonGroup, Paper, Typography } from '@mui/material';
import {
    CategoryScale,
    Chart as ChartJS,
    ChartOptions,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    TimeScale,
    Title,
    Tooltip,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import zoomPlugin from 'chartjs-plugin-zoom';
import { format, subDays, subHours } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useMemo, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    TimeScale,
    zoomPlugin
);

export type TimeRange = '2h' | '6h' | '12h' | '1d' | '7d' | 'all';

interface SensorChartProps {
    selectedSensorIds: number[];
    isFridgeSelected: boolean;
    storeId: number | null;
    temperatureThresholdMin: number;
    temperatureThresholdMax: number;
    currentPageSensorIds?: number[];
}

const SensorChart = ({
    selectedSensorIds,
    isFridgeSelected,
    storeId,
    temperatureThresholdMin,
    temperatureThresholdMax,
    currentPageSensorIds = [],
}: SensorChartProps) => {
    const [timeRange, setTimeRange] = useState<TimeRange>('6h');
    const chartRef = useRef<ChartJS<'line'>>(null);

    // Determine which sensors to fetch
    const sensorIdsToFetch = useMemo(() => {
        if (selectedSensorIds.length > 0 || isFridgeSelected) {
            return selectedSensorIds;
        }
        return currentPageSensorIds.length > 0 ? currentPageSensorIds : undefined;
    }, [selectedSensorIds, currentPageSensorIds, isFridgeSelected]);

    // Calculate time range
    const { startTime, endTime } = useMemo(() => {
        const now = new Date();
        let start: Date;

        switch (timeRange) {
            case '2h':
                start = subHours(now, 2);
                break;
            case '6h':
                start = subHours(now, 6);
                break;
            case '12h':
                start = subHours(now, 12);
                break;
            case '1d':
                start = subDays(now, 1);
                break;
            case '7d':
                start = subDays(now, 7);
                break;
            case 'all':
                // Start from a reasonable past date (e.g., 1 year ago)
                // The actual start will be adjusted based on available data
                start = subDays(now, 365);
                break;
            default:
                start = subHours(now, 6);
        }

        return { startTime: start, endTime: now };
    }, [timeRange]);

    // Fetch sensor history
    const { data: historyData, isLoading } = useSensorHistory({
        storeId,
        sensorIds: sensorIdsToFetch,
        startTime,
        endTime,
    });

    // For "all" time range, find the earliest reading time from the data
    const actualStartTime = useMemo(() => {
        if (timeRange === 'all' && historyData && historyData.length > 0) {
            let earliestTime: Date | null = null;
            historyData.forEach((sensorData) => {
                sensorData.readings.forEach((reading) => {
                    const readingTime = new Date(reading.readingTime);
                    if (!earliestTime || readingTime < earliestTime) {
                        earliestTime = readingTime;
                    }
                });
            });
            return earliestTime || startTime;
        }
        return startTime;
    }, [timeRange, historyData, startTime]);

    // Prepare chart data
    const chartData = useMemo(() => {
        if (!historyData || historyData.length === 0) {
            return {
                datasets: [],
            };
        }

        // Generate colors for each sensor
        const colors = [
            '#1976d2',
            '#d32f2f',
            '#388e3c',
            '#f57c00',
            '#7b1fa2',
            '#0288d1',
            '#c2185b',
            '#5d4037',
            '#455a64',
            '#e64a19',
        ];

        const datasets = historyData.map((sensorData, index) => {
            const color = colors[index % colors.length];
            return {
                label: `${sensorData.equipmentLabel} - ${sensorData.sensorLabel}`,
                data: sensorData.readings.map((reading) => ({
                    x: new Date(reading.readingTime).getTime(),
                    y: reading.temperature,
                })),
                borderColor: color,
                backgroundColor: `${color}40`,
                pointRadius: 2,
                pointHoverRadius: 4,
                borderWidth: 2,
                tension: 0.1,
            };
        });

        // Add threshold lines
        const thresholdMinDataset = {
            label: 'Seuil min',
            data:
                historyData.length > 0
                    ? [
                          {
                              x: new Date(actualStartTime).getTime(),
                              y: temperatureThresholdMin,
                          },
                          {
                              x: new Date(endTime).getTime(),
                              y: temperatureThresholdMin,
                          },
                      ]
                    : [],
            borderColor: '#ff9800',
            backgroundColor: 'transparent',
            borderWidth: 2,
            borderDash: [5, 5],
            pointRadius: 0,
            pointHoverRadius: 0,
        };

        const thresholdMaxDataset = {
            label: 'Seuil max',
            data:
                historyData.length > 0
                    ? [
                          {
                              x: new Date(actualStartTime).getTime(),
                              y: temperatureThresholdMax,
                          },
                          {
                              x: new Date(endTime).getTime(),
                              y: temperatureThresholdMax,
                          },
                      ]
                    : [],
            borderColor: '#f44336',
            backgroundColor: 'transparent',
            borderWidth: 2,
            borderDash: [5, 5],
            pointRadius: 0,
            pointHoverRadius: 0,
        };

        return {
            datasets: [...datasets, thresholdMinDataset, thresholdMaxDataset],
        };
    }, [historyData, actualStartTime, endTime, temperatureThresholdMin, temperatureThresholdMax]);

    // Chart options
    const chartOptions: ChartOptions<'line'> = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            animation: false,
            interaction: {
                mode: 'index' as const,
                intersect: false,
            },
            plugins: {
                legend: {
                    position: 'top' as const,
                    display: historyData && historyData.length > 0,
                },
                title: {
                    display: false,
                },
                tooltip: {
                    callbacks: {
                        title: (items) => {
                            if (items.length > 0 && items[0].parsed.x !== null) {
                                const date = new Date(items[0].parsed.x);
                                return format(date, 'PPpp', { locale: fr });
                            }
                            return '';
                        },
                        label: (context) => {
                            const yValue = context.parsed.y;
                            if (yValue !== null && yValue !== undefined) {
                                return `${context.dataset.label}: ${yValue.toFixed(1)}°C`;
                            }
                            return '';
                        },
                    },
                },
                zoom: {
                    zoom: {
                        wheel: {
                            enabled: true,
                            modifierKey: 'ctrl',
                        },
                        pinch: {
                            enabled: true,
                        },
                        mode: 'x' as const,
                    },
                    pan: {
                        enabled: true,
                        mode: 'x' as const,
                    },
                },
            },
            scales: {
                x: {
                    type: 'time',
                    time: {
                        unit: (() => {
                            if (timeRange === 'all') return 'day';
                            if (timeRange === '7d') return 'day';
                            if (timeRange === '1d') return 'hour';
                            return 'minute';
                        })(),
                        displayFormats: {
                            minute: 'HH:mm',
                            hour: 'dd/MM HH:mm',
                            day: 'dd/MM/yyyy',
                        },
                    },
                    title: {
                        display: true,
                        text: 'Temps',
                    },
                },
                y: {
                    title: {
                        display: true,
                        text: 'Température (°C)',
                    },
                },
            },
        }),
        [historyData, timeRange]
    );

    // Zoom is now handled by chartjs-plugin-zoom via the chart options

    if (!sensorIdsToFetch || sensorIdsToFetch.length === 0) {
        return (
            <Paper sx={{ p: 2, mt: 2 }}>
                <Typography variant="body2" color="text.secondary" align="center">
                    Aucun capteur sélectionné
                </Typography>
            </Paper>
        );
    }

    return (
        <Paper sx={{ p: 2, mt: 2, position: 'relative' }}>
            <Box
                sx={{
                    mb: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Typography variant="h6">Historique des Températures</Typography>
                <ButtonGroup variant="outlined" size="small">
                    <Button
                        variant={timeRange === '2h' ? 'contained' : 'outlined'}
                        onClick={() => setTimeRange('2h')}
                    >
                        2h
                    </Button>
                    <Button
                        variant={timeRange === '6h' ? 'contained' : 'outlined'}
                        onClick={() => setTimeRange('6h')}
                    >
                        6h
                    </Button>
                    <Button
                        variant={timeRange === '12h' ? 'contained' : 'outlined'}
                        onClick={() => setTimeRange('12h')}
                    >
                        12h
                    </Button>
                    <Button
                        variant={timeRange === '1d' ? 'contained' : 'outlined'}
                        onClick={() => setTimeRange('1d')}
                    >
                        1j
                    </Button>
                    <Button
                        variant={timeRange === '7d' ? 'contained' : 'outlined'}
                        onClick={() => setTimeRange('7d')}
                    >
                        7j
                    </Button>
                    <Button
                        variant={timeRange === 'all' ? 'contained' : 'outlined'}
                        onClick={() => setTimeRange('all')}
                    >
                        Tout
                    </Button>
                </ButtonGroup>
            </Box>

            <Box sx={{ height: 400, position: 'relative' }}>
                {isLoading ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                        }}
                    >
                        <Typography>Chargement...</Typography>
                    </Box>
                ) : (
                    <Line ref={chartRef} data={chartData} options={chartOptions} />
                )}
            </Box>
        </Paper>
    );
};

export default SensorChart;
